import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import HeaderV2 from '../components/HeaderV2'
import WholesaleHeroV2 from './wholesale/v2/WholesaleHeroV2'
import WholesaleIntroSplitV2 from './wholesale/v2/WholesaleIntroSplitV2'
import WholesaleAccordionSectionV2 from './wholesale/v2/WholesaleAccordionSectionV2'
import WholesalePartnersCarouselV2 from './wholesale/v2/WholesalePartnersCarouselV2'
import WholesaleGallerySliderV2 from './wholesale/v2/WholesaleGallerySliderV2'
import WholesaleFAQV2 from './wholesale/v2/WholesaleFAQV2'
import WholesaleApplyFormV2 from './wholesale/v2/WholesaleApplyFormV2'
import { wholesaleHero, wholesaleFAQ } from './wholesale/v2/content'
import type { Lang } from './wholesale/v2/types'

/**
 * WholesaleV2Page
 * Barista Map style B2B landing page at /:lang/wholesale/v2.
 * Old /:lang/wholesale stays untouched to preserve existing SEO ranking.
 *
 * SEO preservation strategy:
 *  - All H2 section titles are always rendered (visible),
 *    paragraph bodies always exist in the DOM (only visually collapsed).
 *  - Hero H1 is rendered as sr-only with the original keyword phrase
 *    "商業豆批發｜營業用咖啡豆專業供應" so the V2 page keeps the same
 *    primary keyword signal as the legacy WholesalePage.
 *  - FAQPage Schema.org JSON-LD is injected, identical structure to the
 *    legacy page.
 */
const WholesaleV2Page: React.FC = () => {
  const location = useLocation()
  const currentLang: Lang = location.pathname.startsWith('/en') ? 'en' : 'zh'
  const isPreviewRoute = location.pathname.includes('/v2')

  // Inject Barista Map serif fonts on mount
  useEffect(() => {
    const id = 'wholesale-v2-fonts'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href =
      'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Noto+Serif+TC:wght@300;400;500&family=Noto+Sans+TC:wght@300;400&display=swap'
    document.head.appendChild(link)
  }, [])

  const meta =
    currentLang === 'zh'
      ? {
          title: '商業豆批發｜營業用咖啡豆專業供應 - ONGU Studio',
          description:
            'ONGU Studio 專注服務咖啡廳、早午餐店、餐酒館與各類餐飲品牌，提供穩定風味的商業豆批發與營業用咖啡豆供應。',
        }
      : {
          title: 'Wholesale Coffee Beans for Business - ONGU Studio',
          description:
            'ONGU Studio provides stable commercial coffee bean wholesale for cafes, brunch restaurants, and food brands across Taiwan.',
        }

  // FAQPage JSON-LD — identical structure to legacy WholesalePage
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: wholesaleFAQ.map((item) => ({
      '@type': 'Question',
      name: item.q[currentLang],
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a[currentLang],
      },
    })),
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link
          rel="canonical"
          href={`https://www.ongucoffee.com/${currentLang}/wholesale${isPreviewRoute ? '/v2' : ''}`}
        />
        {/* Hreflang — 換骨後指正式 URL，預覽期間指 /v2 */}
        <link
          rel="alternate"
          hrefLang="zh"
          href={`https://www.ongucoffee.com/zh/wholesale${isPreviewRoute ? '/v2' : ''}`}
        />
        <link
          rel="alternate"
          hrefLang="en"
          href={`https://www.ongucoffee.com/en/wholesale${isPreviewRoute ? '/v2' : ''}`}
        />
        {isPreviewRoute && <meta name="robots" content="noindex,nofollow" />}
        {/* OG */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={wholesaleHero.image} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Solid white HeaderV2 — Hero sits below */}
      <HeaderV2 />
      <div className="h-20" aria-hidden="true" />

      <main>
        <WholesaleHeroV2 currentLang={currentLang} />
        <WholesaleIntroSplitV2 currentLang={currentLang} />
        <WholesaleAccordionSectionV2 currentLang={currentLang} />
        <WholesalePartnersCarouselV2 currentLang={currentLang} />
        <WholesaleGallerySliderV2 currentLang={currentLang} />
        <WholesaleApplyFormV2 currentLang={currentLang} />
        <WholesaleFAQV2 currentLang={currentLang} />
      </main>
    </div>
  )
}

export default WholesaleV2Page
